import {
  ComponentType,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  createContext,
  isValidElement,
  useContext,
  useState,
} from 'react';
import { Children } from 'react';

import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { Tooltip } from '@components/tooltip';
import { theme } from '@styles/theme';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

export type ToggleGroupProps<T extends string | number = string> = {
  onChange(value: T): void;
  children:
    | Array<ReactElement<ToggleIconButtonProps<T>>>
    | Array<ReactElement<ToggleButtonProps<T>>>;
  value?: T;
  defaultValue?: T;
  fullWidth?: boolean;
};

type DefaultToggleButtonProps = Omit<
  HTMLAttributes<HTMLButtonElement>,
  'value' | 'onClick' | 'children'
>;

const ToggleContext = createContext<{
  selected?: string | number;
  onSelect(value: string | number): void;
}>({
  selected: undefined,
  onSelect: noop,
});

const Root = <T extends string | number = string>({
  children,
  defaultValue,
  value: userValue,
  onChange,
  fullWidth = false,
}: ToggleGroupProps<T>) => {
  const [localValue, setLocalValue] = useState<T | undefined>(() => {
    if (userValue !== undefined) return userValue;
    return defaultValue;
  });

  const actualValue = userValue ?? localValue;

  const isIconGroup = checkIsIconGroup(children);

  return (
    <ToggleContext.Provider
      value={{
        selected: actualValue,
        onSelect: (value) => {
          if (userValue === undefined) {
            setLocalValue(value as any);
          }
          onChange(value as any);
        },
      }}
    >
      <Container $fullWidth={fullWidth} $variant={isIconGroup ? 'icon' : 'text'}>
        {children}
      </Container>
    </ToggleContext.Provider>
  );
};

Root.displayName = 'ToggleGroup.Root';

type ToggleIconButtonProps<T extends string | number> = DefaultToggleButtonProps & {
  icon: ComponentType<IconProps>;
  value: T;
  size?: 'md' | 'lg';
};

const ToggleIconButton = <T extends string | number>({
  icon: Icon,
  value,
  size = 'md',
  ...rest
}: ToggleIconButtonProps<T>) => {
  const { selected, onSelect } = useContext(ToggleContext);
  return (
    <IconContainer
      disabled={selected === value}
      data-active={selected === value}
      data-size={size}
      {...rest}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(value);
      }}
    >
      <Icon />
    </IconContainer>
  );
};
ToggleIconButton.displayName = 'ToggleGroup.IconButton';

type ToggleButtonProps<T extends string | number> = DefaultToggleButtonProps & {
  text: string;
  value: T;
  variant?: 'red' | 'orange' | 'green' | 'purple' | 'blue';
  tooltip?: string;
};

const ToggleButton = <T extends string | number>({
  text,
  value,
  variant = 'purple',
  tooltip,
  ...rest
}: ToggleButtonProps<T>) => {
  const { selected, onSelect } = useContext(ToggleContext);

  const content = (
    <TextContainer
      disabled={selected === value}
      data-active={selected === value}
      data-variant={variant}
      {...rest}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(value);
      }}
    >
      {text}
    </TextContainer>
  );
  if (!tooltip) {
    return content;
  }
  return <Tooltip content={tooltip}>{content}</Tooltip>;
};
ToggleButton.displayName = 'ToggleGroup.Button';

export const ToggleGroup = {
  Root,
  IconButton: ToggleIconButton,
  Button: ToggleButton,
};

const Container = styled.div<{ $variant: 'icon' | 'text'; $fullWidth: boolean }>((p) => ({
  borderRadius: pxToRem(8),
  overflow: 'hidden',
  display: 'flex',
  flexShrink: 0,
  width: p.$fullWidth ? '100%' : 'max-content',
  gap: pxToRem(1),
}));

const TextContainer = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: transitionStandard('color', 'background'),
  border: 'none',
  outline: 'none',
  background: theme.color.uiTrans5,
  height: theme.base.height,
  padding: `0 ${pxToRem(16)}`,
  font: theme.font.semibold14,
  color: theme.color.textSecondary,
  width: '100%',

  '&[data-active="false"]:hover, &:focus-visible': {
    cursor: 'pointer',
    backgroundColor: theme.color.uiTrans10,
  },

  '&[data-active="true"]': {
    color: theme.color.white100,

    '&[data-variant=orange]': {
      backgroundColor: theme.color.orange100,
    },
    '&[data-variant=green]': {
      backgroundColor: theme.color.green100,
    },
    '&[data-variant=red]': {
      backgroundColor: theme.color.red100,
    },
    '&[data-variant=purple]': {
      backgroundColor: theme.color.purple50,
    },
    '&[data-variant=blue]': {
      backgroundColor: theme.color.elementBlue100,
    },
  },
});

const IconContainer = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: transitionStandard('color', 'background'),
  border: 'none',
  outline: 'none',
  background: theme.color.uiTrans5,
  '--icon-color': theme.color.buttonIconPositive,
  width: '100%',

  '&[data-active="true"]': {
    '--icon-color': theme.color.elementBlue100,
  },

  '&[data-size="md"]': {
    padding: pxToRem(4),
  },

  '&[data-size="lg"]': {
    padding: pxToRem(8),
  },

  '&[data-active="false"]:hover, &:focus-visible': {
    cursor: 'pointer',
    background: theme.color.uiTrans10,
  },
});

const checkIsIconGroup = (children: ReactNode) => {
  const legitChildren = Children.toArray(children).filter(isValidElement);
  const childrenTypes = legitChildren.map((child) => child.type);
  const childrenTypesSet = [...new Set(childrenTypes)];
  if (childrenTypesSet.length !== 1) {
    throw new Error('ToggleGroup: All children must be of the same type.');
  }
  return legitChildren[0].type === ToggleIconButton;
};
